
import React from "react";
import { TabsContent } from "@/components/ui/tabs";
import { BankAccountsStatus, Invoice, Filters, InvoiceStatus } from "@/types/payments";
import InvoicesSection from "@/components/payments/InvoicesSection";

interface TabContentProps {
  activeTab: string;
  selectedInvoices: string[];
  filteredInvoices: Invoice[];
  sortedInvoices: Invoice[];
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  filters: Filters;
  activeFilters: Filters;
  handleFilterChange: (key: keyof Filters, value: any) => void;
  handleStatusFilterChange?: (status: InvoiceStatus | 'all') => void; 
  resetFilters: () => void;
  applyFilters: () => void;
  uniqueCurrencies: string[];
  toggleSelectInvoice: (invoiceId: string) => void;
  toggleSelectAll: () => void;
  handleSort: (key: keyof Invoice) => void;
  renderSortIcon: (key: keyof Invoice) => React.ReactNode;
  handleBulkPayment: () => void;
  handleBulkCancel: () => void;
  handleBulkExport: () => void;
  updatePaymentDate: (invoiceId: string, date: Date) => void;
  onRowClick: (invoice: Invoice) => void;
  handleSinglePay: (invoiceId: string, paymentDate: Date) => void;
  handleSingleCancel: (invoiceId: string) => void;
  handleSingleExport: (invoiceId: string) => void;
  handleDownloadInvoice: (invoiceId: string, fileName: string) => void;
  onEditPayment: (invoiceId: string) => void;
  onEditVendor: (invoiceId: string) => void;
  onEditInvoice: (invoiceId: string) => void;
  handleConfirmMatch?: (payableId: string, transactionId: string) => Promise<boolean>;
  allInvoices: Invoice[];
  getStatusBadge: (status: string) => any;
  formatCurrency: (amount: number, currency: string) => string;
  /** Pass-through to InvoiceActionMenu/PayableMatchSuggestionsModal — see BankAccountsStatus's
   * docstring (#498 whole-branch review finding I2: must preserve 'loading'/'error', never
   * collapse to `false`). */
  hasBankAccounts?: BankAccountsStatus;
}

const TabContent: React.FC<TabContentProps> = ({
  activeTab,
  selectedInvoices,
  filteredInvoices,
  sortedInvoices,
  showFilters,
  setShowFilters,
  filters,
  activeFilters,
  handleFilterChange,
  handleStatusFilterChange,
  resetFilters,
  applyFilters,
  uniqueCurrencies,
  toggleSelectInvoice,
  toggleSelectAll,
  handleSort,
  renderSortIcon,
  handleBulkPayment,
  handleBulkCancel,
  handleBulkExport,
  updatePaymentDate,
  onRowClick,
  handleSinglePay,
  handleSingleCancel,
  handleSingleExport,
  handleDownloadInvoice,
  onEditPayment,
  onEditVendor,
  onEditInvoice,
  handleConfirmMatch,
  allInvoices,
  getStatusBadge,
  formatCurrency,
  hasBankAccounts
}) => {
  return (
    <TabsContent value={activeTab} className="mt-0">
      <InvoicesSection 
        activeTab={activeTab}
        selectedInvoices={selectedInvoices}
        filteredInvoices={filteredInvoices}
        sortedInvoices={sortedInvoices}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        filters={filters}
        activeFilters={activeFilters}
        handleFilterChange={handleFilterChange}
        handleStatusFilterChange={handleStatusFilterChange}
        resetFilters={resetFilters}
        applyFilters={applyFilters}
        uniqueCurrencies={uniqueCurrencies}
        toggleSelectInvoice={toggleSelectInvoice}
        toggleSelectAll={toggleSelectAll}
        handleSort={handleSort}
        renderSortIcon={renderSortIcon}
        handleBulkPayment={handleBulkPayment}
        handleBulkCancel={handleBulkCancel}
        handleBulkExport={handleBulkExport}
        updatePaymentDate={updatePaymentDate}
        onRowClick={onRowClick}
        handleSinglePay={handleSinglePay}
        handleSingleCancel={handleSingleCancel}
        handleSingleExport={handleSingleExport}
        handleDownloadInvoice={handleDownloadInvoice}
        onEditPayment={onEditPayment}
        onEditVendor={onEditVendor}
        onEditInvoice={onEditInvoice}
        handleConfirmMatch={handleConfirmMatch}
        allInvoices={allInvoices}
        getStatusBadge={getStatusBadge}
        formatCurrency={formatCurrency}
        hasBankAccounts={hasBankAccounts}
      />
    </TabsContent>
  );
};

export default TabContent;
